import { Autocomplete, TextField, SxProps, Paper } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useState } from 'react';

interface AutocompleteOption {
  value: string;
  name: string;
}

interface StyledAutocompleteProps {
  sx?: SxProps;
  value?: string | null;
  label?: string;
  placeholder?: string;
  options: AutocompleteOption[];
  disabled?: boolean;
  disabledOnDark?: boolean;
  onChange: (value: string) => void;
}

export default function StyledAutocomplete({
  sx,
  value,
  onChange,
  label,
  placeholder,
  options,
  disabled = false,
  disabledOnDark = false
}: StyledAutocompleteProps) {
  const theme = useTheme();
  const [inputValue, setInputValue] = useState('');

  const selectedOption = options.find((option) => option.value === value) || null;

  return (
    <Autocomplete
      value={selectedOption}
      inputValue={inputValue}
      onInputChange={(event, newInputValue) => {
        setInputValue(newInputValue);
      }}
      onChange={(event, newValue) => {
        onChange(newValue?.value || '');
        setInputValue(newValue?.name || '');
      }}
      options={options}
      getOptionLabel={(option) => option.name}
      isOptionEqualToValue={(option, value) => option.value === value.value}
      disabled={disabled}
      popupIcon={<KeyboardArrowDownIcon />}
      PaperComponent={(props) => (
        <Paper
          {...props}
          sx={{
            borderRadius: '8px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
            marginTop: '4px',
            '& .MuiAutocomplete-option': {
              fontSize: '14px',
              padding: '10px 16px',
              transition: 'background-color 0.2s ease-in-out',
              '&:hover': {
                backgroundColor: `${theme.palette.primary.main}10`
              },
              '&.Mui-focused': {
                backgroundColor: `${theme.palette.primary.main}20`
              }
            }
          }}
        />
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          variant="outlined"
          size="small"
          InputLabelProps={{
            ...params.InputLabelProps,
            shrink: true,
            sx: {
              color: theme.palette.text.secondary,
              fontSize: '14px',
              fontWeight: 500,
              transform: 'translate(16px, -9px) scale(0.75)',
              '&.Mui-focused': {
                color: theme.palette.primary.main
              }
            }
          }}
          InputProps={{
            ...params.InputProps,
            sx: {
              '& input': {
                padding: '12px 16px',
                fontSize: '14px',
                fontWeight: 400,
                textAlign: 'left'
              },
              '& input::placeholder': {
                color: theme.palette.text.secondary,
                opacity: 0.7
              }
            }
          }}
          sx={{
            backgroundColor: disabled
              ? disabledOnDark
                ? '#bdbdbd'
                : theme.palette.action.disabledBackground
              : theme.palette.background.default,
            minWidth: '12em',
            height: '48px',
            borderRadius: '8px',
            transition: 'all 0.2s ease-in-out',
            boxShadow: disabled ? 'none' : '0 1px 3px rgba(0, 0, 0, 0.08)',
            '&:hover': disabled
              ? {}
              : {
                  boxShadow: '0 2px 6px rgba(0, 0, 0, 0.12)',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.primary.main,
                    borderWidth: '2px'
                  }
                },
            '& .MuiOutlinedInput-root': {
              borderRadius: '8px',
              height: '48px',
              '& fieldset': {
                borderColor: disabled ? 'rgba(0, 0, 0, 0.06)' : 'rgba(0, 0, 0, 0.12)',
                borderWidth: '1px'
              },
              '&:hover fieldset': disabled
                ? {}
                : {
                    borderColor: theme.palette.primary.main,
                    borderWidth: '2px'
                  },
              '&.Mui-focused fieldset': disabled
                ? {}
                : {
                    borderColor: theme.palette.primary.main,
                    borderWidth: '2px',
                    boxShadow: `0 0 0 3px ${theme.palette.primary.main}20`
                  },
              '&.Mui-disabled fieldset': {
                borderColor: 'rgba(0, 0, 0, 0.06)'
              }
            },
            '& .MuiSvgIcon-root': {
              fontSize: '20px',
              color: disabled ? theme.palette.action.disabled : theme.palette.text.secondary,
              transition: 'all 0.2s ease-in-out'
            },
            '&:hover .MuiSvgIcon-root': disabled
              ? {}
              : {
                  color: theme.palette.primary.main
                },
            '& .MuiAutocomplete-popupIndicator': {
              '&:hover': disabled
                ? {}
                : {
                    backgroundColor: 'transparent'
                  }
            },
            ...sx
          }}
        />
      )}
      sx={{
        '& .MuiAutocomplete-popupIndicator': {
          transform: 'none',
          '&[data-popup-open="true"]': {
            transform: 'rotate(180deg)'
          }
        }
      }}
    />
  );
}
